import { React } from '@@commons';
import { useEffect, useState } from 'react';
import { HtmlContent } from '@inwink/react-utils';
import { fetchAlertBannerConfig } from './utils';
import type { VisualTheme } from '@inwink/entities/visualtheme';

import './alertbanner.less';

export interface IAlertBannerProps {
    language: string;
    isCms?: boolean;
}

interface IAlertBannerState {
    loading: boolean;
    enabled: boolean;
    level: number;
    text: VisualTheme.IAppLabel;
    hidden: boolean;
}

const STORAGE_KEY = 'inwink.bo.alertbanner.hidden';

const levelToClass = (lvl: number) => {
    switch (lvl) {
        case 1: return 'alert';
        case 2: return 'warning';
        default: return 'info';
    }
};

export const AlertBanner = ({ language, isCms }: IAlertBannerProps) => {
    const [state, setState] = useState<IAlertBannerState>({
        loading: true,
        enabled: false,
        level: 3,
        text: {},
        hidden: false
    });

    const load = () => {
        fetchAlertBannerConfig().then((cfg) => {
            if (cfg) {
                setState((s) => ({
                    ...s,
                    enabled: cfg.enabled ?? false,
                    level: cfg.level ?? 3,
                    text: cfg.text || {},
                    loading: false,
                    hidden: isCms ? localStorage.getItem(STORAGE_KEY) === 'true' : false
                }));
            } else {
                setState((s) => ({ ...s, loading: false }));
            }
        }, () => setState((s) => ({ ...s, loading: false })));
    };

    const close = () => {
        localStorage.setItem(STORAGE_KEY, 'true');
        window.dispatchEvent(new Event('inwink-alertbanner-refresh'));
        setState((s) => ({ ...s, hidden: true }));
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        const refresh = () => {
            setState((s) => ({ ...s }));
            load();
        };
        window.addEventListener('inwink-alertbanner-refresh', refresh);
        return () => window.removeEventListener('inwink-alertbanner-refresh', refresh);
    }, []);

    if (state.loading || (isCms && state.hidden) || !state.enabled) {
        return null;
    }

    const content = state.text?.[language];

    if (!content) {
        return null;
    }

    return <div className={`bo-alertbanner ${isCms ? 'bo-alertbanner--cms' : ''} bo-alertbanner-${levelToClass(state.level)}`}>
        <HtmlContent
            key={"bo-alertbanner-content"}
            className="bo-alertbanner-content"
            content={content}
        />
        {isCms && <button type="button" className="bo-alertbanner-close" onClick={close}>×</button>}
    </div>;
};
